import React, { useRef } from 'react';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import {
  ExtendedTableLowCode,
  ExtendedTableLowCodeRef,
} from '@/components/shared/table/ExtendedTableLowCode';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import { CustomCellProperties } from 'alurkerja-ui';
import { MediaItem } from '@/components/mediaPreview/MediaItem';
import moment from 'moment';
import { IDetailTestRunSchema } from '@/api/testRunDetail';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';
import { RichTextContent } from '@/components/shared/RichTextContent';

interface Media {
  id: string;
  name: string;
  path: string;
  size: string;
  created_at: string;
  updated_at: string;
}

type StatusHistoryTestRunProps = {
  testRun: IDetailTestRunSchema;
};

export const StatusHistoryTestRun: React.FC<StatusHistoryTestRunProps> = ({
  testRun,
}) => {
  const ref = useRef<ExtendedTableLowCodeRef>(null);

  const spec = useCustomSpec(`/api/crud/result`, {
    columns: [
      { key: 'comment_test', label: 'Comments' },
      { key: 'status', label: 'Status' },
      { key: 'assignee_id', label: 'Assigned To' },
      { key: 'created_at', label: 'Created Date' },
      { key: 'medias', label: 'Attachments' },
    ],
  });

  const customCell = ({
    name,
    value,
    rowValue,
    defaultCell,
  }: CustomCellProperties): React.ReactElement => {
    if (name === 'comment_test') {
      return <RichTextContent content={value} className="max-w-2xl min-w-[18rem]" />;
    } else if (name === 'status') {
      return <span className="whitespace-nowrap">{defaultCell}</span>;
    } else if (name === 'medias' && Array.isArray(value)) {
      const medias = value as Media[];
      return (
        <div>
          {medias.map(media => (
            <MediaItem key={media.id} media={media} />
          ))}
        </div>
      );
    } else if (name === 'created_at') {
      return (
        <div className="flex flex-col whitespace-nowrap text-xs">
          <span>{moment(value).format('DD MMM YYYY')}</span>
          <span className="text-gray-500">{moment(value).format('HH:mm:ss')}</span>
        </div>
      );
    } else if (name === 'assignee_id') {
      return <>{rowValue.test_run_detail?.assignee?.full_name}</>;
    }

    return <>{defaultCell}</>;
  };

  if (!spec) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <div className="[&_#table\_wrapper]:px-0 [&_#table\_wrapper]:max-h-[calc(100vh-24rem)] [&_#table\_wrapper]:overflow-auto [&_#table\_wrapper_th]:sticky [&_#table\_wrapper_th]:top-0 [&_#table\_wrapper_th]:z-10 [&_#table\_wrapper_th]:bg-[#F3F6F9] [&_#table\_wrapper_td]:align-top">
      <ExtendedTableLowCode
        ref={ref}
        defaultSort='created_at'
        defaultSortOrder='desc'
        defaultFilter={{ test_run_detail_id: testRun?.id }}
        spec={spec}
        customCell={customCell}
        showFilter={false}
        showCheckbox={false}
        customButtonCreate={HideCustomCreateButton}
        hideActionColumn
      />
    </div>
  );
};
